import { useContext } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { AppContext, NotificationType } from 'components/AppContext/AppContext';
import { FileInfo } from 'models/FileInfo';
import { HttpMethod } from 'models/HttpMethod';
import { LogLevel } from 'models/LogLevel';
import { useNavigate } from 'react-router-dom';

export const useImageUploader = (apiBaseUrl: string) => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const { notify, sendLog } = useContext(AppContext);

  const toBase64 = (file: File) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });

  const uploadSingleImage = async (
    file: File,
    projectId: number,
    onComplete: (file: FileInfo | undefined) => void,
  ) => {
    const fileStream = await toBase64(file);
    const fileData = [{ name: file.name, content: fileStream }];
    try {
      const token = await getAccessTokenSilently();
      if (!token) {
        navigate('/');
      } else {
        const settings: RequestInit = {
          method: HttpMethod.POST,
          mode: 'cors',
          cache: 'no-cache',
          credentials: 'include',
          body: JSON.stringify(fileData),
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        };
        const result = await fetch(
          `${apiBaseUrl}/api/Attachments?ProjectId=${projectId}`,
          settings,
        );
        if (result.ok) {
          const uploadedFiles = await result.json();
          onComplete(uploadedFiles[0]);
        }
      }
    } catch (e: any) {
      notify('Unable to upload image.', NotificationType.Error);
      sendLog('Unable to upload image.', LogLevel.error, e);
    }
  };

  const handleUploadFromEditor = async (upload: any, data: any) => {
    const projectId = parseInt(data.data.get('projectId'), 10);

    const onComplete = (fileResponse: FileInfo | undefined) => {
      if (!fileResponse) return;

      const response = {
        file: {
          url: fileResponse.url,
          id: fileResponse.name,
        },
      };
      upload.complete(response, data.e);
    };

    // eslint-disable-next-line no-restricted-syntax
    for (const key in data.files) {
      if (typeof data.files[key] === 'object') {
        const file = data.files[key];
        uploadSingleImage(file, projectId, onComplete);
      }
    }
  };

  const handleCoverImageUpload = async (
    fileList: FileList | null,
    projectId: number,
    onComplete: (file: FileInfo | undefined) => void,
  ) => {
    const file: File = fileList![0];
    await uploadSingleImage(file, projectId, onComplete);
  };

  return { handleUploadFromEditor, handleCoverImageUpload };
};
